// Backend registration boundary for deterministic project intake.
//
// File Explorer materialisation is intentionally local-first because browser
// File/Blob handles cannot be sent to PostgreSQL.  This module is the explicit
// boundary that runs after extraction + local media migration and before any
// Generate/Prepare action is allowed to spend AI money.

import { resolveProjectQuoteReference } from './project-reference.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function firstNonBlank(...values) {
    for (const value of values) {
        const cleaned = cleanString(value);
        if (cleaned) return cleaned;
    }
    return '';
}

function leafName(value) {
    const normalised = cleanString(value).replace(/\\/g, '/').replace(/\/+$/, '');
    return normalised ? normalised.slice(normalised.lastIndexOf('/') + 1).trim() : '';
}

function isUsableDisplayName(value) {
    const text = cleanString(value);
    return !!text
        && !UUID_RE.test(text)
        && !['untitled project', 'queued project', 'project details loading…'].includes(text.toLowerCase());
}

export function resolveProjectDisplayName(project) {
    const meta = project?.meta && typeof project.meta === 'object' ? project.meta : {};
    const folder = project?.projectFolder && typeof project.projectFolder === 'object'
        ? project.projectFolder
        : {};
    const candidates = [
        // Fresh queued imports intentionally keep two identities: the source
        // folder label helps the operator recognise the Queue row, while
        // `title` is the canonical quote title carried into Stage 5.
        project?.sourceDisplayName,
        project?.title,
        meta.displayName,
        project?.name,
        project?.folderName,
        folder.subfolderName,
        leafName(project?.syncRelativePath ?? project?.relativePath ?? project?.path),
    ];
    return candidates.find(isUsableDisplayName)?.trim() || 'Untitled project';
}

export function resolveQueueQuoteReference(project) {
    return resolveProjectQuoteReference(project).quoteRef;
}

export function buildQueueDisplayMeta(project) {
    const meta = project?.meta && typeof project.meta === 'object' ? project.meta : {};
    const formData = project?.formData && typeof project.formData === 'object' ? project.formData : {};
    const relativePath = firstNonBlank(
        project?.syncRelativePath,
        project?.relativePath,
        project?.path,
        project?.projectFolder?.relativePath,
        project?.projectFolder?.sourcePath,
    );
    const reference = resolveProjectQuoteReference(project);
    return {
        projectId: cleanString(project?.id),
        name: resolveProjectDisplayName(project),
        address: firstNonBlank(meta.siteAddress, project?.address, project?.siteAddress, formData.address),
        quoteRef: reference.quoteRef,
        rawFolderQuoteRef: reference.rawFolderQuoteRef,
        quoteRefSource: reference.source,
        relativePath,
    };
}

function hasTransientDocumentBinary(document) {
    if (!document || typeof document !== 'object') return false;
    const values = [
        document.bytes,
        document.file,
        document.blob,
        document.pdfBlob,
        document.pdfFile,
        document.docBytes,
        document.arrayBuffer,
        document.data,
        document.pdfDataUrl,
    ];
    return values.some((value) => {
        if (typeof value === 'string') return value.startsWith('data:') || value.startsWith('blob:');
        if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
        return typeof ArrayBuffer !== 'undefined'
            && (value instanceof ArrayBuffer || ArrayBuffer.isView(value));
    });
}

function backendSyncError(error, projectId) {
    if ([
        'PROJECT_BACKEND_IDENTITY_MISMATCH',
        'PROJECT_BACKEND_NEWER_THAN_LOCAL',
        'PROJECT_BACKEND_GENERATED_STATE_CONFLICT',
        'PROJECT_BACKEND_AUTHORITATIVE_INPUT_CONFLICT',
        'PROJECT_BACKEND_PROJECT_IDENTITY_CONFLICT',
        'PROJECT_LOCAL_CHANGED_DURING_BACKEND_RECONCILIATION',
        'PROJECT_RESTART_LINEAGE_NOT_PERSISTED',
        'PROJECT_RESTART_LINEAGE_CONFLICT',
        'PROJECT_IDENTITY_CONFLICT',
        'INTAKE_HANDOFF_REQUIRED',
        'PROJECT_STALE_WRITE',
        // Already wrapped below: an outer registration step must not hide it
        // behind a second "could not register" stop.
        'DOCUMENT_NOT_STORED',
    ].includes(error?.code)) return error;
    const wrapped = new Error(
        `QuoteMaker could not register project ${projectId} with the backend before quote preparation: `
        + `${error?.message || String(error || 'unknown error')}`,
        error ? { cause: error } : undefined,
    );
    // A reference document the file service refused (data-service-api.js
    // _projectDocumentDurabilityError marks it final): no reconnect or retry
    // stores it, so the job says which file and offers a fresh pick of its
    // files (import-workflow.ts, 2026-10-04 UAT F2).
    const documentRefused = error?.code === 'DOCUMENT_DURABILITY_REQUIRED' && error?.retryable === false;
    wrapped.code = documentRefused ? 'DOCUMENT_NOT_STORED' : 'PROJECT_BACKEND_SYNC_FAILED';
    wrapped.category = 'project-persistence';
    wrapped.projectId = projectId;
    wrapped.retryable = error?.retryable === false
        ? false
        : ![400, 401, 403, 422].includes(Number(error?.status || error?.statusCode));
    wrapped.operatorAction = documentRefused
        ? 'Pick its files again and leave that file out, or save it as a PDF or Word file first.'
        : 'Reconnect QuoteMaker, then retry this queued project. Its local extracted files were preserved.';
    wrapped.status = error?.status || error?.statusCode || null;
    // Carry the inner throw site's bounded diagnostics. Without this the queue
    // row recorded only this wrapper's sentence, so a document that storage
    // refused reached the operator as "could not register this project with
    // the cloud service" and nothing else.
    if (error?.details && typeof error.details === 'object') wrapped.details = error.details;
    return wrapped;
}

// Shared freshness boundary for every Folder 01 + 02 retry path. Keep this
// exported: queue recovery must use the same definition as backend
// reconciliation or a Stage 1 row carrying Stage 1.5+ artefacts can be
// mistaken for untouched intake and retain stale generated work.
export function projectHasGeneratedWork(project) {
    if (!project || typeof project !== 'object') return false;
    const stage = Number(project.currentStage ?? project.current_stage ?? 0);
    // Deterministic intake itself is persisted at Stage 1. A POST can commit and
    // its verification GET can still fail transiently; the next retry must be
    // allowed to re-save that same untouched draft. Stage 2+ is unambiguously
    // beyond intake, while Stage 1 is safe only when its status and generated
    // artefacts below still describe an intake-only record.
    if (Number.isFinite(stage) && stage > 1) return true;
    const completedStages = project.completedStages ?? project.completed_stages;
    if (Array.isArray(completedStages) && completedStages.length > 0) return true;

    const populated = (value) => {
        if (typeof value === 'string') return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (value && typeof value === 'object') return Object.keys(value).length > 0;
        return value !== null && value !== undefined;
    };
    if (populated(project.riskAnalysis ?? project.risk_analysis)) return true;
    if (populated(project.photoAnalysis ?? project.photo_analysis)) return true;
    if (populated(project.scope)) return true;
    if (populated(project.quoteData ?? project.quote_data)) return true;

    // Stage 1.5 and later generators can persist useful work while the project
    // still reports currentStage 1. A retry must treat those artefacts as
    // backend-ahead state too; otherwise the deterministic intake envelope can
    // silently replace a summary (or a later derived record) produced after the
    // original POST committed.
    const derivedArtifacts = [
        project.projectSummary ?? project.project_summary,
        project.projectStrategy ?? project.project_strategy,
        project.wms ?? project.projectWMS ?? project.project_wms,
        project.costingSheet ?? project.costing_sheet,
        project.workMethodOverallProgramme ?? project.work_method_overall_programme,
        project.aiGeneratedItems ?? project.ai_generated_items,
        project.originalAiScope ?? project.original_ai_scope,
        project.appliedLearnings ?? project.applied_learnings,
        project.kpiReport ?? project.kpi_report,
        project.selectedHeroImage ?? project.selected_hero_image,
    ];
    if (derivedArtifacts.some(populated)) return true;

    const status = cleanString(project.status).toLowerCase();
    const intakeOnlyStatus = [
        'draft',
        'pre-processed',
        'preprocessed',
        'ready_for_analysis',
        'ready-for-analysis',
        'intake_complete',
    ].includes(status);
    if (Number.isFinite(stage) && stage === 1) return !intakeOnlyStatus;
    return !!status && !intakeOnlyStatus;
}

function backendNewerThanIntake(projectId, backendProject) {
    const error = new Error(
        `Backend project ${projectId} already contains generated quote work, so deterministic intake will not overwrite it.`,
    );
    error.code = 'PROJECT_BACKEND_NEWER_THAN_LOCAL';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = false;
    error.operatorAction = 'Open Project History and choose whether to continue the existing quote or import these files as a new project.';
    error.details = {
        backendStatus: backendProject?.status || null,
        backendCurrentStage: backendProject?.currentStage ?? backendProject?.current_stage ?? null,
    };
    return error;
}

const TRANSIENT_PERSISTENCE_KEYS = new Set([
    'bytes', 'file', 'blob', 'handle', 'fileHandle', 'directoryHandle',
    'pdfBlob', 'pdfFile', 'docBytes', 'arrayBuffer', 'data', 'dataUrl',
    'signedUrl', 'signed_url',
    'preview', 'previewUrl', 'preview_url',
    'thumbnail', 'thumbnailUrl', 'thumbnail_url',
    'objectUrl', 'object_url',
    'docBlobId', 'doc_blob_id',
    'blobId', 'blob_id',
    'videoBlobId', 'video_blob_id',
    'localBlobId', 'local_blob_id',
    's3Key', 's3_key',
    'pdfS3Key', 'pdf_s3_key',
    'fileS3Key', 'file_s3_key',
    'fileMetadataId', 'file_metadata_id',
    'cloudObjectKey', 'cloud_object_key',
    'storageKey', 'storage_key',
    'workerProxy', 'worker_proxy',
    'workerKeyframes', 'worker_keyframes',
    'cloudPlayback', 'cloud_playback',
    'uploadProgress', 'upload_progress',
    'uploadState', 'upload_state',
    'uploadStatus', 'upload_status',
    'uploadedAt', 'uploaded_at',
    'storageProgress', 'storage_progress',
    'storageState', 'storage_state',
    'storageStatus', 'storage_status',
    'storedAt', 'stored_at',
    'uploadReceipt', 'upload_receipt',
    'uploadReceipts', 'upload_receipts',
    'storageReceipt', 'storage_receipt',
    'storageReceipts', 'storage_receipts',
    'persistenceReceipt', 'persistence_receipt',
    'persistenceReceipts', 'persistence_receipts',
    'cloudStorageReceipt', 'cloud_storage_receipt',
    'durabilityReceipt', 'durability_receipt',
    'persistenceVerified', 'persistence_verified',
    'localBlobOnly', 'local_blob_only',
    'localMemoryOnly', 'local_memory_only',
    'pendingValidation', 'pending_validation',
    'pendingValidationAt', 'pending_validation_at',
    'forcePhotoUpload', 'force_photo_upload',
    'skipBlobMigration', 'skip_blob_migration',
    'cloudMedia', 'cloud_media',
]);

// The background video proxy upload (video-cloud-upload.ts) stamps a whole
// family onto video rows while a project is open: cloudProxyUploadStatus,
// ...Attempts, ...RetryAfter, ...Error, ...ErrorCode, ...NeedsPersistence,
// ...RetryWakeAt. They record how far a storage upload has got, not what the
// footage shows. The family was never listed above, so an upload finishing
// during a Quote Run read as an evidence change: it stopped Stage 1 with
// PROJECT_RUN_STALE (videos) and made the local and cloud copies of the same
// project look like they carried different builder inputs. Matched by prefix so
// a new field in the family cannot reopen the gap.
function isStorageProgressKey(key) {
    return key.startsWith('_local')
        || key.startsWith('_cloud')
        || key.startsWith('cloudProxy')
        || key.startsWith('cloud_proxy');
}

function canonicalComparable(value) {
    if (Array.isArray(value)) return value.map(canonicalComparable);
    if (!value || typeof value !== 'object') return value ?? null;
    const out = {};
    for (const key of Object.keys(value).sort()) {
        if (TRANSIENT_PERSISTENCE_KEYS.has(key) || isStorageProgressKey(key)) continue;
        const inner = value[key];
        if (key === 'url' && typeof inner === 'string' && inner.startsWith('blob:')) continue;
        out[key] = canonicalComparable(inner);
    }
    return out;
}

// A Project can legitimately describe the same persisted evidence differently
// on two devices. IndexedDB ids, live File/Blob handles and object URLs locate
// bytes in one browser profile only; they are not evidence identity and must
// never decide whether a cloud row is safe to replace. Everything else is
// retained, including unknown future metadata, so ambiguity fails closed.
const NON_SEMANTIC_EVIDENCE_KEYS = new Set([
    ...TRANSIENT_PERSISTENCE_KEYS,
    // Cloud and browser storage receipts answer where the same bytes live,
    // not what those bytes mean. Fresh intake can legitimately leave the
    // local row with an IndexedDB locator while the verified backend row has
    // the S3/file_metadata locator created during that save.
    'contentType', 'content_type',
    'mimeType', 'mime_type',
]);

// The server cleans every string it stores (backend/src/utils/postgres-sanitize.ts:
// PostgreSQL cannot hold NUL, and MSG/PDF extraction leaks control characters).
// The browser copy is not cleaned, so text carrying one stray form feed made an
// untouched project differ from its own saved copy. Same rule as the server,
// applied to BOTH sides, so it is exact rather than a tolerance: a difference
// made only of characters the server cannot store is not a builder input.
// Keep byte for byte aligned with sanitizeForPostgres.
const UNSTORABLE_TEXT_CHARS = /[ --]/g;
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

function asStoredByTheServer(text) {
    const wellFormed = typeof text.toWellFormed === 'function'
        ? text.toWellFormed()
        : text.replace(LONE_SURROGATE, '�');
    return wellFormed.replace(UNSTORABLE_TEXT_CHARS, '');
}

function semanticAuthorityComparable(value) {
    if (typeof value === 'function' || typeof value === 'undefined') return undefined;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return undefined;
    if (typeof File !== 'undefined' && value instanceof File) return undefined;
    if (typeof ArrayBuffer !== 'undefined'
        && (value instanceof ArrayBuffer || ArrayBuffer.isView(value))) return undefined;
    if (typeof value === 'string') {
        if (value.startsWith('blob:') || value.startsWith('data:')) return undefined;
        return asStoredByTheServer(value);
    }
    if (value === null || typeof value !== 'object') return value ?? null;
    if (Array.isArray(value)) {
        return value.map(item => semanticAuthorityComparable(item) ?? null);
    }
    const out = {};
    for (const key of Object.keys(value).sort()) {
        if (NON_SEMANTIC_EVIDENCE_KEYS.has(key) || isStorageProgressKey(key)) continue;
        const inner = value[key];
        if ((key === 'url' || key === 'data' || key === 'dataUrl' || key === 'pdfDataUrl')
            && typeof inner === 'string'
            && (inner.startsWith('blob:') || inner.startsWith('data:'))) continue;
        const comparable = semanticAuthorityComparable(inner);
        if (comparable !== undefined) out[key] = comparable;
    }
    return out;
}

/**
 * Builder-authored inputs and source evidence that generated work depends on.
 * Lifecycle tokens and generated outputs are compared elsewhere. This narrow
 * projection prevents a queue retry from treating matching scope as proof that
 * the cloud still carries the same notes or evidence set.
 */
export function projectAuthoritativeInputProjection(project) {
    return semanticAuthorityComparable({
        formData: project?.formData ?? project?.form_data ?? {},
        documents: project?.documents ?? [],
        photos: project?.photos ?? [],
        videos: project?.videos ?? [],
        videoTranscripts: project?.videoTranscripts ?? project?.video_transcripts ?? [],
        preprocessedFiles: project?.preprocessedFiles ?? project?.preprocessed_files ?? [],
    });
}

// ─── Inline media the save path removes on purpose ───────────────────────────
//
// Live, 2026-09-20: a queued Neutral Bay project failed the instant its turn
// came, non-retryable, "contains newer or different builder inputs (documents)".
// Nobody had touched it. Its RFQ is an .eml whose extracted text carries an
// inline image as base64. data-service-api.js (_stripCustomerMediaFromString)
// replaces such a token with the marker below in the JSON it POSTs, and by
// design leaves the browser's own copy alone, so the server held the marker and
// this browser held the bytes. Compared byte for byte, the two copies of one
// untouched project could never agree. Importing one project at a time never
// runs this comparison, which is why only queued imports failed.
//
// The rule stays fail closed. Text is accepted as the same only when the
// backend string is the local string with whole encoded tokens swapped for the
// marker, checked from the backend's own markers outward. Any other difference,
// including one a character long, still stops the run.
//
// What this cannot see, by construction: WHICH image stood there. The server
// never holds those bytes, so no comparison could tell two images apart. The
// words around them are still compared exactly.
const INLINE_MEDIA_REDACTION = '[inline customer media removed]';
const REDACTED_TOKEN_RE = /^(?:data:[^,\s"'<>]*,[A-Za-z0-9+/_=-]*|[A-Za-z0-9+/_=-]{32,})$/;
const MEDIA_DATA_URL_START_RE = /^data:(?:image|video)\/[^,\s"'<>]*,/i;
// A value the save path drops outright rather than marks: nothing but encoded
// media. Long enough that an id, a hash or a storage key can never qualify.
const WHOLE_VALUE_MEDIA_MIN_CHARS = 100;

// The save path removes a QUOTED media data URL up to its closing quote, line
// breaks and all (an HTML attribute wrapped by the mail client). Accepted only
// between a matching pair of quotes, exactly as the save path bounds it.
function isQuotedMediaDataUrl(local, position, end) {
    const closingQuote = local[end];
    if (closingQuote !== '"' && closingQuote !== "'") return false;
    let before = position - 1;
    while (before >= 0 && (local[before] === ' ' || local[before] === '\t')) before -= 1;
    if (before < 0 || local[before] !== closingQuote) return false;
    const removed = local.slice(position, end);
    return MEDIA_DATA_URL_START_RE.test(removed) && !removed.includes(closingQuote);
}

function isWholeValueMedia(value) {
    if (typeof value !== 'string') return false;
    const parts = value.split(/\s+/).filter(Boolean);
    return parts.length > 0
        && value.length >= WHOLE_VALUE_MEDIA_MIN_CHARS
        && parts.every(part => REDACTED_TOKEN_RE.test(part));
}

function sameTextApartFromRedactedMedia(local, backend) {
    if (local === backend) return true;
    if (typeof local !== 'string' || typeof backend !== 'string') return false;
    if (!backend.includes(INLINE_MEDIA_REDACTION)) return false;
    const segments = backend.split(INLINE_MEDIA_REDACTION);
    if (!local.startsWith(segments[0])) return false;
    let position = segments[0].length;
    for (let index = 1; index < segments.length; index += 1) {
        const segment = segments[index];
        const last = index === segments.length - 1;
        let end;
        if (last) {
            if (!local.endsWith(segment)) return false;
            end = local.length - segment.length;
        } else {
            // Two removals with nothing authored between them cannot be told
            // apart from one. Leave that to the strict comparison.
            if (segment === '') return false;
            end = local.indexOf(segment, position);
        }
        if (end < position) return false;
        if (!REDACTED_TOKEN_RE.test(local.slice(position, end))
            && !isQuotedMediaDataUrl(local, position, end)) return false;
        position = end + segment.length;
    }
    return position === local.length;
}

function sameApartFromRedactedMedia(local, backend) {
    if (typeof local === 'string' || typeof backend === 'string') {
        return sameTextApartFromRedactedMedia(local, backend);
    }
    if (Array.isArray(local) || Array.isArray(backend)) {
        if (!Array.isArray(local) || !Array.isArray(backend) || local.length !== backend.length) return false;
        // By index, not every(): every() skips holes and would wave one through.
        for (let index = 0; index < local.length; index += 1) {
            if (!sameApartFromRedactedMedia(local[index], backend[index])) return false;
        }
        return true;
    }
    if (local && backend && typeof local === 'object' && typeof backend === 'object') {
        const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
        // Nothing may appear on the server that this browser does not hold.
        if (!Object.keys(backend).every(key => has(local, key))) return false;
        return Object.keys(local).every(key => (has(backend, key)
            ? sameApartFromRedactedMedia(local[key], backend[key])
            // The save path drops a value that is nothing but media.
            : isWholeValueMedia(local[key])));
    }
    return local === backend;
}

function authoritativeInputChangedLanes(localProject, backendProject) {
    const local = projectAuthoritativeInputProjection(localProject);
    const backend = projectAuthoritativeInputProjection(backendProject);
    return Object.keys(local).filter(key => (
        JSON.stringify(local[key]) !== JSON.stringify(backend[key])
        && !sameApartFromRedactedMedia(local[key], backend[key])
    ));
}

function authoritativeInputConflict(projectId, localProject, backendProject, changedLanes) {
    const error = new Error(
        `Backend project ${projectId} contains newer or different builder inputs (${changedLanes.join(', ')}).`,
    );
    error.code = 'PROJECT_BACKEND_AUTHORITATIVE_INPUT_CONFLICT';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = false;
    error.operatorAction = 'Open the backend project from Project History and review the changed notes or evidence before retrying. QuoteMaker did not overwrite either copy.';
    error.details = {
        changedLanes,
        localUpdatedAt: localProject?.updatedAt ?? localProject?.updated_at ?? null,
        backendUpdatedAt: backendProject?.updatedAt ?? backendProject?.updated_at ?? null,
    };
    return error;
}

export function assertProjectAuthoritativeInputsCompatible(projectId, localProject, backendProject) {
    if (!localProject || String(localProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, localProject?.id, 'local compatibility check');
    }
    if (!backendProject || String(backendProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, backendProject?.id, 'compatibility check');
    }
    const changedLanes = authoritativeInputChangedLanes(localProject, backendProject);
    if (changedLanes.length > 0) {
        throw authoritativeInputConflict(projectId, localProject, backendProject, changedLanes);
    }
}

function localReconciliationSnapshot(project) {
    const source = project && typeof project === 'object' ? project : {};
    const withoutProof = { ...source };
    delete withoutProof.backendVerificationMode;
    delete withoutProof.backendVerifiedAt;
    delete withoutProof.backendVerifiedProjectId;
    delete withoutProof.backendVerifiedUpdatedAt;
    return JSON.stringify(canonicalComparable(withoutProof));
}

function localChangedDuringReconciliation(projectId) {
    const error = new Error(
        `Local project ${projectId} changed while backend reconciliation was running.`,
    );
    error.code = 'PROJECT_LOCAL_CHANGED_DURING_BACKEND_RECONCILIATION';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = true;
    error.operatorAction = 'Retry from the latest local project state. QuoteMaker did not attach stale backend verification to the changed project.';
    return error;
}

async function persistProofIfLocalProjectUnchanged(targetDb, project, proof) {
    if (!targetDb) return;
    if (!targetDb?.projects?.get || !targetDb?.projects?.update) {
        throw new Error('Backend reconciliation requires readable and writable local project storage');
    }
    const expectedSnapshot = localReconciliationSnapshot(project);
    const operation = async () => {
        const current = await targetDb.projects.get(project.id);
        if (!current || localReconciliationSnapshot(current) !== expectedSnapshot) {
            throw localChangedDuringReconciliation(project.id);
        }
        await targetDb.projects.update(project.id, proof);
    };
    if (typeof targetDb.transaction === 'function') {
        await targetDb.transaction('rw', targetDb.projects, operation);
        return;
    }
    await operation();
}

function normalisedCompletedStages(project) {
    const raw = project?.completedStages ?? project?.completed_stages;
    const values = raw instanceof Set ? Array.from(raw) : (Array.isArray(raw) ? raw : []);
    return Array.from(new Set(
        values.map(Number).filter(Number.isInteger),
    )).sort((a, b) => a - b);
}

/**
 * quoteData as generated quote STATE, without the queue runner's own readiness
 * note.
 *
 * pipeline-runner.js persistQuoteReadinessReview writes
 * quoteData.meta.readinessReview with db.projects.put: to this browser's row
 * only, never to the backend. It is review metadata about the draft ("needs a
 * human look, not blocking"), not quote content another device could have
 * authored differently. Counting it here made every project with a readiness
 * finding conflict with ITSELF at the next stage boundary: identical version
 * token, identical stage, 13 of 14 lanes equal, stopped as
 * PROJECT_BACKEND_GENERATED_STATE_CONFLICT (non-retryable) after a complete
 * Stage 3. Live, 2026-09-20. The note still reaches the backend with the next
 * ordinary project save; it just does not decide whether two copies diverge.
 */
function quoteDataWithoutLocalReadinessReview(quoteData) {
    if (!quoteData || typeof quoteData !== 'object' || Array.isArray(quoteData)) return quoteData ?? null;
    const meta = quoteData.meta;
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return quoteData;
    const { readinessReview: _localReviewNote, ...restMeta } = meta;
    const { meta: _meta, ...rest } = quoteData;
    // Applied to BOTH copies, so an empty `meta` on one side and no `meta` on
    // the other compare equal once the note is set aside.
    return Object.keys(restMeta).length > 0 ? { ...rest, meta: restMeta } : rest;
}

function generatedStateProjection(project) {
    return canonicalComparable({
        currentStage: Number(project?.currentStage ?? project?.current_stage ?? 0) || 0,
        completedStages: normalisedCompletedStages(project),
        riskAnalysis: project?.riskAnalysis ?? project?.risk_analysis ?? null,
        photoAnalysis: project?.photoAnalysis ?? project?.photo_analysis ?? null,
        projectSummary: project?.projectSummary ?? project?.project_summary ?? null,
        projectStrategy: project?.projectStrategy ?? project?.project_strategy ?? null,
        wms: project?.wms ?? project?.projectWMS ?? project?.project_wms ?? null,
        costingSheet: project?.costingSheet ?? project?.costing_sheet ?? null,
        workMethodOverallProgramme: project?.workMethodOverallProgramme
            ?? project?.work_method_overall_programme
            ?? null,
        scope: project?.scope ?? [],
        quoteData: quoteDataWithoutLocalReadinessReview(project?.quoteData ?? project?.quote_data ?? null),
        originalAiScope: project?.originalAiScope ?? project?.original_ai_scope ?? null,
        aiGeneratedItems: project?.aiGeneratedItems ?? project?.ai_generated_items ?? {},
        appliedLearnings: project?.appliedLearnings ?? project?.applied_learnings ?? [],
        kpiReport: project?.kpiReport ?? project?.kpi_report ?? null,
        selectedHeroImage: project?.selectedHeroImage ?? project?.selected_hero_image ?? null,
    });
}

function projectUpdatedAtMs(project) {
    const parsed = Date.parse(project?.updatedAt ?? project?.updated_at ?? '');
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Both copies carry the same server version token.
 *
 * projects.updated_at is the optimistic-concurrency token: every content write
 * from any device or tab advances it (CLAUDE.md, "projects.updated_at is the
 * operator's save token"). So when the local row and the backend row hold the
 * SAME token, the local body was mirrored at that server version. Whatever
 * still differs between the two representations is, in practice, one of:
 *   - this browser's own local-only notes (the queue runner writes
 *     quoteData.meta.readinessReview with db.projects.put, never to the API);
 *   - the wire projection being lossy ON PURPOSE (project-projector.ts drops or
 *     hashes fingerprints over 4096 characters, strips inline media; the API
 *     sanitises control characters).
 * Comparing those byte for byte stopped a project as a non-retryable
 * PROJECT_BACKEND_GENERATED_STATE_CONFLICT three times in one live session
 * (2026-09-20), each time with identical tokens and identical stages. A backend
 * that is genuinely ahead still fails below as 'backend-newer'.
 *
 * One known exception to 'mirrored at that version' (review, 2026-09-20): the
 * narrow lifecycle mirrors in data-service.js (recordOutcome, recordFollowUp,
 * setCrmLink) stamp the server's CURRENT token onto the local row without
 * fetching its body. A browser that was already behind can therefore hold a
 * newer token over an older generated body. What happens then is safe: builder
 * inputs are still compared separately and still stop the run, nothing is
 * pushed from the stale body, and the canonical server copy is adopted. The
 * difference is recorded below so it leaves a trail rather than passing unseen.
 */
function sameServerVersion(localProject, backendProject) {
    const localUpdatedAt = projectUpdatedAtMs(localProject);
    const backendUpdatedAt = projectUpdatedAtMs(backendProject);
    const same = localUpdatedAt !== null && backendUpdatedAt !== null && localUpdatedAt === backendUpdatedAt;
    if (same) {
        // Lane NAMES only. quoteData alone is the two known benign causes.
        const localLanes = generatedStateProjection(localProject);
        const backendLanes = generatedStateProjection(backendProject);
        const beyondQuoteData = Object.keys(localLanes).filter(lane => lane !== 'quoteData'
            && JSON.stringify(localLanes[lane]) !== JSON.stringify(backendLanes[lane]));
        if (beyondQuoteData.length > 0) {
            console.warn(`[project-intake-persistence] Same server version, generated state differs beyond quoteData (${beyondQuoteData.join(', ')}). Adopting the backend copy.`);
        }
    }
    return same;
}

function generatedStateConflict(projectId, localProject, backendProject, reason) {
    const error = new Error(
        `Backend project ${projectId} contains generated quote state that does not match this browser's local copy.`,
    );
    error.code = reason === 'backend-newer'
        ? 'PROJECT_BACKEND_NEWER_THAN_LOCAL'
        : 'PROJECT_BACKEND_GENERATED_STATE_CONFLICT';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = false;
    error.operatorAction = 'Open the backend project from Project History and review it before retrying. QuoteMaker did not overwrite either copy.';
    // Lane NAMES only. Without them this stop reads the same whether one
    // review note differs or the whole quote does.
    const localLanes = generatedStateProjection(localProject);
    const backendLanes = generatedStateProjection(backendProject);
    error.details = {
        reason,
        changedLanes: Object.keys(localLanes)
            .filter(lane => JSON.stringify(localLanes[lane]) !== JSON.stringify(backendLanes[lane])),
        localUpdatedAt: localProject?.updatedAt ?? localProject?.updated_at ?? null,
        backendUpdatedAt: backendProject?.updatedAt ?? backendProject?.updated_at ?? null,
        localCurrentStage: localProject?.currentStage ?? localProject?.current_stage ?? null,
        backendCurrentStage: backendProject?.currentStage ?? backendProject?.current_stage ?? null,
    };
    return error;
}

function projectIdentityConflict(projectId, fields) {
    const error = new Error(
        `Backend project ${projectId} disagrees with this browser on ${fields.join(', ')}.`,
    );
    error.code = 'PROJECT_BACKEND_PROJECT_IDENTITY_CONFLICT';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = false;
    error.operatorAction = 'Open the backend project from Project History and confirm the correct client/project identity before retrying.';
    error.details = { fields };
    return error;
}

function projectIdentityConflictFields(localProject, backendProject) {
    const localForm = localProject?.formData && typeof localProject.formData === 'object'
        ? localProject.formData
        : {};
    const backendForm = backendProject?.formData && typeof backendProject.formData === 'object'
        ? backendProject.formData
        : {};
    // Queue source labels intentionally differ from the canonical quote title.
    // Identity compares only persisted title lanes, never sourceDisplayName or
    // sync-folder display metadata.
    const localTitle = firstNonBlank(
        localProject?.title,
        localProject?.projectTitle,
        localForm.projectTitle,
    );
    const backendTitle = firstNonBlank(
        backendProject?.title,
        backendProject?.projectTitle,
        backendForm.projectTitle,
    );
    const identityPairs = {
        // Do not let a synthetic UUID or "Untitled project" placeholder become
        // persistence identity evidence when one side simply omitted a title.
        title: [
            isUsableDisplayName(localTitle) ? localTitle : '',
            isUsableDisplayName(backendTitle) ? backendTitle : '',
        ],
        address: [
            firstNonBlank(localProject?.address, localProject?.siteAddress, localForm.address),
            firstNonBlank(backendProject?.address, backendProject?.siteAddress, backendForm.address),
        ],
        quoteRef: [resolveQueueQuoteReference(localProject), resolveQueueQuoteReference(backendProject)],
        companyId: [
            localProject?.companyId ?? localProject?.company_id ?? localForm.clientCompanyId ?? null,
            backendProject?.companyId ?? backendProject?.company_id ?? backendForm.clientCompanyId ?? null,
        ],
        propertyId: [
            localProject?.propertyId ?? localProject?.property_id ?? localForm.propertyId ?? null,
            backendProject?.propertyId ?? backendProject?.property_id ?? backendForm.propertyId ?? null,
        ],
    };
    return Object.entries(identityPairs)
        .filter(([, [localValue, backendValue]]) => (
            cleanString(String(localValue ?? ''))
            && cleanString(String(backendValue ?? ''))
            && cleanString(String(localValue)).toLowerCase() !== cleanString(String(backendValue)).toLowerCase()
        ))
        .map(([field]) => field);
}

function assertProjectIdentityCompatible(projectId, localProject, backendProject) {
    if (!localProject || String(localProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, localProject?.id, 'local compatibility check');
    }
    if (!backendProject || String(backendProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, backendProject?.id, 'compatibility check');
    }
    const identityConflicts = projectIdentityConflictFields(localProject, backendProject);
    if (identityConflicts.length > 0) {
        throw projectIdentityConflict(projectId, identityConflicts);
    }
}

/**
 * Batch-load gate for a Project already reconciled with the backend. The
 * canonical cloud read may replace only browser-specific media locators. Any
 * changed builder input, identity, or generated quote state stops the run.
 */
export function assertProjectCloudSnapshotCompatible(projectId, localProject, backendProject) {
    assertProjectIdentityCompatible(projectId, localProject, backendProject);
    assertProjectAuthoritativeInputsCompatible(projectId, localProject, backendProject);

    const localState = JSON.stringify(generatedStateProjection(localProject));
    const backendState = JSON.stringify(generatedStateProjection(backendProject));
    if (localState === backendState) return;
    if (sameServerVersion(localProject, backendProject)) return;

    const localUpdatedAt = projectUpdatedAtMs(localProject);
    const backendUpdatedAt = projectUpdatedAtMs(backendProject);
    if (backendUpdatedAt !== null
        && (localUpdatedAt === null || backendUpdatedAt > localUpdatedAt)) {
        throw generatedStateConflict(projectId, localProject, backendProject, 'backend-newer');
    }
    throw generatedStateConflict(projectId, localProject, backendProject, 'generated-state-diverged');
}

function assertBackendProjectCompatible(projectId, localProject, backendProject, {
    requireUntouchedExisting = false,
} = {}) {
    if (!localProject || String(localProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, localProject?.id, 'local compatibility check');
    }
    if (!backendProject || String(backendProject.id || '') !== String(projectId)) {
        throw identityMismatch(projectId, backendProject?.id, 'compatibility check');
    }
    const localGenerated = projectHasGeneratedWork(localProject);
    const backendGenerated = projectHasGeneratedWork(backendProject);
    if (requireUntouchedExisting && backendGenerated && !localGenerated) {
        throw backendNewerThanIntake(projectId, backendProject);
    }
    if (!localGenerated || !backendGenerated) return;

    assertProjectIdentityCompatible(projectId, localProject, backendProject);

    const localState = JSON.stringify(generatedStateProjection(localProject));
    const backendState = JSON.stringify(generatedStateProjection(backendProject));
    if (localState !== backendState && !sameServerVersion(localProject, backendProject)) {
        const localUpdatedAt = projectUpdatedAtMs(localProject);
        const backendUpdatedAt = projectUpdatedAtMs(backendProject);
        if (localUpdatedAt !== null && backendUpdatedAt !== null && backendUpdatedAt > localUpdatedAt) {
            throw generatedStateConflict(projectId, localProject, backendProject, 'backend-newer');
        }
        throw generatedStateConflict(projectId, localProject, backendProject, 'generated-state-diverged');
    }
    assertProjectAuthoritativeInputsCompatible(projectId, localProject, backendProject);
}

function identityMismatch(projectId, actualId, source) {
    const error = new Error(
        `Backend ${source} returned project ${actualId || '(missing id)'} while QuoteMaker was registering ${projectId}.`,
    );
    error.code = 'PROJECT_BACKEND_IDENTITY_MISMATCH';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.actualProjectId = actualId || null;
    error.retryable = false;
    error.operatorAction = 'Stop and review Project History before retrying; QuoteMaker refused to continue with a different project identity.';
    return error;
}

function backendEnabledByDefault() {
    return import.meta.env?.VITE_USE_BACKEND !== 'false'
        && import.meta.env?.VITE_USE_BACKEND !== false;
}

function backendProofPatch(projectId, backendProject, verifiedAt) {
    return {
        backendVerificationMode: 'backend',
        backendVerifiedAt: verifiedAt,
        backendVerifiedProjectId: projectId,
        backendVerifiedUpdatedAt: backendProject?.updatedAt ?? backendProject?.updated_at ?? null,
    };
}

function backendDisabledProofPatch() {
    return {
        backendVerificationMode: 'disabled',
        backendVerifiedAt: null,
        backendVerifiedProjectId: null,
        backendVerifiedUpdatedAt: null,
    };
}

function projectFolderObject(project) {
    const folder = project?.projectFolder ?? project?.project_folder;
    return folder && typeof folder === 'object' && !Array.isArray(folder) ? folder : null;
}

function restartLineage(project) {
    const folder = projectFolderObject(project) || {};
    const restartOfProjectId = cleanString(
        folder.restartOfProjectId
        ?? folder.restart_of_project_id
        ?? project?.restartOfProjectId
        ?? project?.restart_of_project_id,
    );
    if (!restartOfProjectId) return null;
    const revisionNumberRaw = folder.revisionNumber
        ?? folder.revision_number
        ?? project?.revisionNumber
        ?? project?.revision_number;
    const revisionNumber = Number.isFinite(Number(revisionNumberRaw)) && Number(revisionNumberRaw) > 0
        ? Number(revisionNumberRaw)
        : null;
    const revisionSuffix = cleanString(
        folder.revisionSuffix
        ?? folder.revision_suffix
        ?? project?.revisionSuffix
        ?? project?.revision_suffix,
    ) || (revisionNumber ? `-R${revisionNumber}` : null);

    return {
        restartOfProjectId,
        intakeAttemptKind: cleanString(
            folder.intakeAttemptKind
            ?? folder.intake_attempt_kind
            ?? project?.intakeAttemptKind
            ?? project?.intake_attempt_kind,
        ) || 'restart-from-source',
        intakeRestartedAt: cleanString(
            folder.intakeRestartedAt
            ?? folder.intake_restarted_at
            ?? project?.intakeRestartedAt
            ?? project?.intake_restarted_at,
        ) || null,
        ...(revisionNumber ? { revisionNumber } : {}),
        ...(revisionSuffix ? { revisionSuffix } : {}),
    };
}

function projectFolderWithRestartLineage(project) {
    const folder = projectFolderObject(project);
    const lineage = restartLineage(project);
    if (!lineage) return folder;
    return {
        ...(folder || {}),
        ...lineage,
    };
}

function backendHasRestartLineage(backendProject, expectedLineage) {
    if (!expectedLineage) return true;
    const actual = restartLineage(backendProject);
    return !!actual
        && actual.restartOfProjectId === expectedLineage.restartOfProjectId
        && actual.intakeAttemptKind === expectedLineage.intakeAttemptKind
        && actual.intakeRestartedAt === expectedLineage.intakeRestartedAt;
}

function restartLineageNotPersisted(projectId, expectedLineage) {
    const error = new Error(
        `Backend project ${projectId} did not retain its clean-restart lineage, so QuoteMaker will not start AI preparation.`,
    );
    error.code = 'PROJECT_RESTART_LINEAGE_NOT_PERSISTED';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = true;
    error.operatorAction = 'Retry this Folder 01 + 02 restart. The local source files and prior audit project were preserved.';
    error.details = { expectedRestartOfProjectId: expectedLineage?.restartOfProjectId || null };
    return error;
}

function restartLineageConflict(projectId, predecessorIds) {
    const predecessors = Array.from(new Set(
        (Array.isArray(predecessorIds) ? predecessorIds : [])
            .map(value => cleanString(String(value || '')))
            .filter(Boolean),
    ));
    const error = new Error(
        `Queue audit for project ${projectId} names multiple clean-restart predecessors. QuoteMaker will not choose one automatically.`,
    );
    error.code = 'PROJECT_RESTART_LINEAGE_CONFLICT';
    error.category = 'project-persistence';
    error.projectId = projectId;
    error.retryable = false;
    error.operatorAction = 'Review Project History and keep the correct restart attempt before preparing this quote.';
    error.details = { predecessorProjectIds: predecessors };
    return error;
}

function queueAuditRestartLineage(projectId, jobs) {
    const candidates = [];
    for (const job of Array.isArray(jobs) ? jobs : []) {
        if (String(job?.projectId || '') === String(projectId)) {
            const direct = restartLineage(job);
            if (direct) candidates.push(direct);
        }
        const replacementProjectId = cleanString(
            job?.reviewDetails?.replacementProjectId
            ?? job?.review_details?.replacement_project_id,
        );
        const recordsRestart = job?.reviewCode === 'RESTARTED_AS_NEW_ATTEMPT'
            || job?.review_code === 'RESTARTED_AS_NEW_ATTEMPT'
            || job?.completionOutcome === 'restarted-as-new-attempt'
            || job?.completion_outcome === 'restarted-as-new-attempt';
        const predecessorId = cleanString(job?.projectId);
        if (recordsRestart
            && replacementProjectId === String(projectId)
            && UUID_RE.test(predecessorId)
            && predecessorId !== String(projectId)) {
            candidates.push({
                restartOfProjectId: predecessorId,
                intakeAttemptKind: 'restart-from-source',
                intakeRestartedAt: cleanString(
                    job?.reviewDetails?.restartedAt
                    ?? job?.review_details?.restarted_at
                    ?? job?.completedAt
                    ?? job?.completed_at,
                ) || null,
            });
        }
    }
    const predecessorIds = Array.from(new Set(
        candidates.map(candidate => candidate.restartOfProjectId),
    ));
    if (predecessorIds.length > 1) {
        throw restartLineageConflict(projectId, predecessorIds);
    }
    if (predecessorIds.length === 0) return null;
    const samePredecessor = candidates.filter(candidate => (
        candidate.restartOfProjectId === predecessorIds[0]
    ));
    return {
        restartOfProjectId: predecessorIds[0],
        intakeAttemptKind: samePredecessor
            .map(candidate => cleanString(candidate.intakeAttemptKind))
            .find(Boolean) || 'restart-from-source',
        intakeRestartedAt: samePredecessor
            .map(candidate => cleanString(candidate.intakeRestartedAt))
            .find(Boolean) || null,
    };
}

async function recoverRestartLineageFromLocalAudit(project, targetDb) {
    if (!project || typeof project !== 'object') return project;
    const projectId = project.id;
    let storedProject = null;
    if (targetDb?.projects?.get) {
        storedProject = await targetDb.projects.get(projectId);
    }
    const directCandidates = [restartLineage(project), restartLineage(storedProject)].filter(Boolean);
    const directPredecessors = Array.from(new Set(
        directCandidates.map(candidate => candidate.restartOfProjectId),
    ));
    if (directPredecessors.length > 1) {
        throw restartLineageConflict(projectId, directPredecessors);
    }

    let auditLineage = null;
    if (targetDb?.batch_jobs?.toArray) {
        auditLineage = queueAuditRestartLineage(projectId, await targetDb.batch_jobs.toArray());
    }
    const firstLineage = directCandidates[0] || auditLineage;
    if (!firstLineage) return storedProject || project;
    if (auditLineage
        && auditLineage.restartOfProjectId !== firstLineage.restartOfProjectId) {
        throw restartLineageConflict(projectId, [
            firstLineage.restartOfProjectId,
            auditLineage.restartOfProjectId,
        ]);
    }
    const matchingLineage = [...directCandidates, auditLineage].filter(candidate => (
        candidate?.restartOfProjectId === firstLineage.restartOfProjectId
    ));
    const expectedLineage = {
        restartOfProjectId: firstLineage.restartOfProjectId,
        intakeAttemptKind: matchingLineage
            .map(candidate => cleanString(candidate.intakeAttemptKind))
            .find(Boolean) || 'restart-from-source',
        intakeRestartedAt: matchingLineage
            .map(candidate => cleanString(candidate.intakeRestartedAt))
            .find(Boolean) || null,
    };

    const baseProject = storedProject || project;
    const recoveredProject = {
        ...baseProject,
        ...expectedLineage,
        projectFolder: {
            ...(projectFolderObject(baseProject) || {}),
            ...expectedLineage,
        },
    };
    if (!targetDb) return recoveredProject;
    if (!targetDb?.projects?.update || !targetDb?.projects?.get) {
        throw restartLineageNotPersisted(projectId, expectedLineage);
    }
    await targetDb.projects.update(projectId, {
        ...expectedLineage,
        projectFolder: recoveredProject.projectFolder,
    });
    const verified = await targetDb.projects.get(projectId);
    if (!backendHasRestartLineage(verified, expectedLineage)) {
        throw restartLineageNotPersisted(projectId, expectedLineage);
    }
    return verified;
}

function assertRestartLineagePersisted(projectId, localProject, backendProject) {
    const expectedLineage = restartLineage(localProject);
    if (expectedLineage && !backendHasRestartLineage(backendProject, expectedLineage)) {
        throw restartLineageNotPersisted(projectId, expectedLineage);
    }
}

function identityOnlyRegistrationEnvelope(project, registeredAt) {
    const formData = project?.formData && typeof project.formData === 'object'
        ? project.formData
        : {};
    const quoteRef = resolveQueueQuoteReference(project);
    return {
        id: project.id,
        title: resolveProjectDisplayName(project),
        address: firstNonBlank(project?.address, project?.siteAddress, formData.address),
        quoteNumber: quoteRef || null,
        status: 'draft',
        currentStage: 1,
        completedStages: [],
        formData: {
            ...formData,
            notes: cleanString(formData.notes),
            address: firstNonBlank(formData.address, project?.address, project?.siteAddress),
            clientName: firstNonBlank(formData.clientName, project?.clientName),
            clientCompany: firstNonBlank(formData.clientCompany, project?.clientCompany),
            strataplan: firstNonBlank(formData.strataplan, project?.strataPlan),
            orderNumber: quoteRef || '',
            intakeJobId: formData.intakeJobId || project?.intakeJobId || undefined,
            intakeFolderName: formData.intakeFolderName || project?.intakeFolderName || undefined,
            intakeParentBucket: formData.intakeParentBucket || project?.intakeParentBucket || undefined,
            intakeVisitEvidenceCount: formData.intakeVisitEvidenceCount || project?.intakeVisitEvidenceCount || undefined,
        },
        scope: [],
        photos: [],
        documents: [],
        videos: [],
        videoTranscripts: [],
        projectFolder: projectFolderWithRestartLineage(project),
        workflowMode: project?.workflowMode ?? project?.workflow_mode ?? undefined,
        updated_at: registeredAt,
    };
}

/**
 * Reconcile a local project UUID with PostgreSQL before a queue is allowed to
 * enter an AI-backed preparation stage.
 *
 * This deliberately performs every network request before the optional Dexie
 * proof update; callers may invoke it immediately before an IndexedDB queue
 * transaction without keeping that transaction open across fetches.
 * `identity-only` is used by orphan retry before deterministic preprocessing:
 * it registers a tiny shell and never sends raw document/photo bytes. `full`
 * is used to recover an already-processed local project whose server row was
 * lost. The backend's createIfMissingOnly contract makes the POST idempotent
 * and prevents a GET→POST race from overwriting an existing quote.
 */
export async function reconcileProjectBackendRegistration(project, options = {}) {
    const projectId = project?.id;
    if (!UUID_RE.test(String(projectId || ''))) {
        const error = new Error(`Backend reconciliation requires a UUID project id; received "${projectId || ''}".`);
        error.code = 'PROJECT_ID_INVALID';
        error.category = 'project-persistence';
        error.retryable = false;
        throw error;
    }

    const backendEnabled = options.backendEnabled !== undefined
        ? options.backendEnabled
        : backendEnabledByDefault();
    const verifiedAt = options.now || new Date().toISOString();
    const targetDb = options.db || null;
    project = await recoverRestartLineageFromLocalAudit(project, targetDb);
    if (!backendEnabled) {
        const proof = backendDisabledProofPatch();
        await persistProofIfLocalProjectUnchanged(targetDb, project, proof);
        return {
            project: { ...project, ...proof },
            backendProject: null,
            backendVerified: false,
            created: false,
        };
    }

    let saveBackendProject = options.saveBackendProject;
    let fetchBackendProject = options.fetchBackendProject;
    if (typeof saveBackendProject !== 'function' || typeof fetchBackendProject !== 'function') {
        const mod = await import('./data-service-api.js');
        saveBackendProject = saveBackendProject || ((payload) => mod.DataServiceAPI.projects.save(payload));
        fetchBackendProject = fetchBackendProject || ((id) => mod.DataServiceAPI.projects.getById(id));
    }

    const registrationMode = options.registrationMode === 'identity-only'
        ? 'identity-only'
        : 'full';
    const registration = registrationMode === 'identity-only'
        ? identityOnlyRegistrationEnvelope(project, verifiedAt)
        : {
            ...project,
            projectFolder: projectFolderWithRestartLineage(project),
        };
    const expectedRestartLineage = restartLineage(project);
    let backendProject = null;
    let backendWasAbsent = false;
    try {
        try {
            backendProject = await fetchBackendProject(projectId);
        } catch (error) {
            const status = Number(error?.status || error?.statusCode);
            if (status !== 404) throw error;
            backendWasAbsent = true;
        }

        if (backendProject) {
            assertBackendProjectCompatible(projectId, project, backendProject, {
                requireUntouchedExisting: options.requireUntouchedExisting === true,
            });
            // A prior bootstrap save can legitimately create the exact UUID
            // before deterministic intake publishes its Folder 01 + 02
            // envelope. Patch that still-untouched shell and verify the
            // lineage read-back before the queue is allowed to enter AI.
            if (expectedRestartLineage
                && !backendHasRestartLineage(backendProject, expectedRestartLineage)) {
                let saveError = null;
                try {
                    // A failed prepare retry can already have matching Stage 1
                    // generated state on both sides while an older backend
                    // save lost only project_folder. Sending the full envelope
                    // with requireUntouchedExisting would correctly reject that
                    // generated row. Use the backend's narrow, row-locked audit
                    // repair in that case so no quote fields are rewritten.
                    const repairPayload = projectHasGeneratedWork(backendProject)
                        ? {
                            id: projectId,
                            projectFolder: projectFolderWithRestartLineage(project),
                            expectedUpdatedAt: backendProject?.updatedAt ?? backendProject?.updated_at ?? project?.expectedUpdatedAt,
                            restartLineageOnly: true,
                        }
                        : {
                            ...registration,
                            id: projectId,
                            projectFolder: projectFolderWithRestartLineage(project),
                            expectedUpdatedAt: backendProject?.updatedAt ?? backendProject?.updated_at ?? project?.expectedUpdatedAt,
                            requireUntouchedExisting: true,
                        };
                    const saved = await saveBackendProject(repairPayload);
                    if (!saved || String(saved.id || '') !== String(projectId)) {
                        throw identityMismatch(projectId, saved?.id, 'restart-lineage save');
                    }
                } catch (error) {
                    saveError = error;
                }
                try {
                    backendProject = await fetchBackendProject(projectId);
                } catch (verificationError) {
                    throw saveError || verificationError;
                }
                if (!backendHasRestartLineage(backendProject, expectedRestartLineage)) {
                    throw saveError || restartLineageNotPersisted(projectId, expectedRestartLineage);
                }
                assertBackendProjectCompatible(projectId, project, backendProject, {
                    requireUntouchedExisting: options.requireUntouchedExisting === true,
                });
            }
        } else {
            let saveError = null;
            try {
                const saved = await saveBackendProject({
                    ...registration,
                    id: projectId,
                    createIfMissingOnly: true,
                });
                if (!saved || String(saved.id || '') !== String(projectId)) {
                    throw identityMismatch(projectId, saved?.id, 'reconciliation save');
                }
            } catch (error) {
                saveError = error;
            }
            if (saveError?.code === 'PROJECT_BACKEND_IDENTITY_MISMATCH'
                || saveError?.code === 'PROJECT_IDENTITY_CONFLICT') {
                throw saveError;
            }

            // Always read back the exact UUID. This recovers an ambiguous
            // network response or a create-only 409 caused by another tab, but
            // never treats the POST response alone as durable proof.
            try {
                backendProject = await fetchBackendProject(projectId);
            } catch (verificationError) {
                throw saveError || verificationError;
            }
            assertBackendProjectCompatible(projectId, project, backendProject, {
                requireUntouchedExisting: options.requireUntouchedExisting === true,
            });
        }

        assertRestartLineagePersisted(projectId, project, backendProject);

        const proof = backendProofPatch(projectId, backendProject, verifiedAt);
        await persistProofIfLocalProjectUnchanged(targetDb, project, proof);
        return {
            project: { ...project, ...proof },
            backendProject,
            backendVerified: true,
            created: backendWasAbsent,
        };
    } catch (error) {
        throw backendSyncError(error, projectId);
    }
}

/**
 * Finalise deterministic intake and prove the same UUID exists on the backend.
 * Local `ready_for_analysis` remains a browser workflow status; the API save
 * layer maps it to the backend's allowed `draft` value on the wire.
 */
export async function persistPreprocessedProject(projectId, options = {}) {
    if (!UUID_RE.test(String(projectId || ''))) {
        const error = new Error(`Pre-processed project id must be a UUID; received "${projectId || ''}".`);
        error.code = 'PROJECT_ID_INVALID';
        error.retryable = false;
        throw error;
    }

    let targetDb = options.db;
    if (!targetDb) {
        const mod = await import('./db.js');
        targetDb = mod.default || mod.db;
    }
    let project = await targetDb.projects.get(projectId);
    if (!project) {
        const error = new Error(`Pre-processed project ${projectId} is missing from the local cache.`);
        error.code = 'PROJECT_NOT_LOCAL';
        error.retryable = false;
        throw error;
    }
    project = await recoverRestartLineageFromLocalAudit(project, targetDb);

    let migrateDocuments = options.migrateDocuments;
    if (typeof migrateDocuments !== 'function') {
        const mod = await import('./document-blob-store.ts');
        migrateDocuments = mod.migrateProjectDocuments;
    }
    const migration = await migrateDocuments(projectId, project.documents || []);
    const documents = Array.isArray(migration?.documents) ? migration.documents : (project.documents || []);
    const unpersistedDocument = documents.find(document => (
        hasTransientDocumentBinary(document)
        && !document?.docBlobId
        && !document?.pdfS3Key
        && !document?.fileMetadataId
    ));
    if (unpersistedDocument) {
        const error = new Error(
            `Document "${unpersistedDocument.name || 'unnamed document'}" could not be moved to durable local storage.`,
        );
        error.code = 'DOCUMENT_BINARY_PERSISTENCE_FAILED';
        error.category = 'project-persistence';
        error.retryable = true;
        error.operatorAction = 'Keep QuoteMaker open, free local disk space if needed, then retry this queued project.';
        throw error;
    }
    // Queue labels prefer the source folder, but persistence owns the quote's
    // canonical/builder-edited title. Do not replace that title with its label.
    const title = isUsableDisplayName(project.title)
        ? project.title.trim()
        : resolveProjectDisplayName(project);
    const readyAt = options.now || new Date().toISOString();
    // Persist the migrated document references immediately, but do not publish
    // a ready lifecycle status yet. A backend outage must leave the project
    // visibly retryable, not falsely ready for Generate.
    await targetDb.projects.update(projectId, {
        title,
        documents,
        backendVerificationMode: null,
        backendVerifiedAt: null,
        backendVerifiedProjectId: null,
        backendVerifiedUpdatedAt: null,
        updated_at: readyAt,
    });
    project = await targetDb.projects.get(projectId);
    const readyProject = {
        ...project,
        status: 'ready_for_analysis',
        preProcessedAt: project.preProcessedAt || readyAt,
        readyForAnalysisAt: readyAt,
        updated_at: readyAt,
    };

    const backendEnabled = options.backendEnabled !== undefined
        ? options.backendEnabled
        : import.meta.env?.VITE_USE_BACKEND !== 'false';
    if (!backendEnabled) {
        await targetDb.projects.update(projectId, {
            status: readyProject.status,
            preProcessedAt: readyProject.preProcessedAt,
            readyForAnalysisAt: readyProject.readyForAnalysisAt,
            backendVerificationMode: 'disabled',
            backendVerifiedAt: null,
            backendVerifiedProjectId: null,
            backendVerifiedUpdatedAt: null,
            updated_at: readyAt,
        });
        project = await targetDb.projects.get(projectId);
        return {
            project,
            backendProject: null,
            backendVerified: false,
            documentsMigrated: Number(migration?.migrated || 0),
            displayMeta: buildQueueDisplayMeta(project),
        };
    }

    let saveBackendProject = options.saveBackendProject;
    let fetchBackendProject = options.fetchBackendProject;
    if (typeof saveBackendProject !== 'function' || typeof fetchBackendProject !== 'function') {
        const mod = await import('./data-service-api.js');
        saveBackendProject = saveBackendProject || ((payload) => mod.DataServiceAPI.projects.save(payload));
        fetchBackendProject = fetchBackendProject || ((id) => mod.DataServiceAPI.projects.getById(id));
    }

    try {
        // Probe before POST. A retry may legitimately find the same untouched
        // draft, but must never replace generated work from an earlier run.
        let existingBackend = null;
        try {
            existingBackend = await fetchBackendProject(projectId);
        } catch (error) {
            const status = Number(error?.status || error?.statusCode);
            if (status !== 404) throw error;
        }
        if (!existingBackend) {
            // Project-scoped upload policies require the parent row to exist
            // before DataServiceAPI can promote local document blobs to cloud
            // storage. Fresh deterministic/File Explorer intake rows only
            // exist in Dexie at this point, so register and verify a byte-free
            // exact-UUID shell first. The create-only reconciliation also
            // closes the cross-tab 404 -> POST race without weakening the
            // later requireUntouchedExisting protection on the full payload.
            const registration = await reconcileProjectBackendRegistration(project, {
                registrationMode: 'identity-only',
                requireUntouchedExisting: true,
                saveBackendProject,
                fetchBackendProject,
                now: readyAt,
            });
            existingBackend = registration.backendProject;
        }
        if (existingBackend) {
            if (String(existingBackend.id || '') !== String(projectId)) {
                throw identityMismatch(projectId, existingBackend?.id, 'preflight');
            }
            if (projectHasGeneratedWork(existingBackend)) {
                throw backendNewerThanIntake(projectId, existingBackend);
            }
        }

        const saved = await saveBackendProject({
            ...readyProject,
            expectedUpdatedAt: existingBackend?.updatedAt ?? existingBackend?.updated_at ?? readyProject.expectedUpdatedAt,
            // Restart metadata is also stored at the local row's top level so
            // it survives older/browser-only intake shapes. Promote it into
            // the backend-owned project_folder JSONB at this first durable
            // boundary rather than relying on a later App save to infer it.
            projectFolder: projectFolderWithRestartLineage(readyProject),
            // The backend re-checks this under its row lock. The preflight GET
            // remains useful operator evidence, but cannot by itself close the
            // cross-device GET→POST race.
            requireUntouchedExisting: true,
        });
        if (!saved || String(saved.id || '') !== String(projectId)) {
            throw identityMismatch(projectId, saved?.id, 'save');
        }
        const verified = await fetchBackendProject(projectId);
        if (!verified || String(verified.id || '') !== String(projectId)) {
            throw identityMismatch(projectId, verified?.id, 'verification');
        }
        // This read-back is the final gate before deterministic intake is
        // promoted to the AI queue. A clean restart without its predecessor
        // UUID is not durably auditable and therefore remains retryable.
        assertRestartLineagePersisted(projectId, readyProject, verified);
        await targetDb.projects.update(projectId, {
            status: readyProject.status,
            preProcessedAt: readyProject.preProcessedAt,
            readyForAnalysisAt: readyProject.readyForAnalysisAt,
            backendVerificationMode: 'backend',
            backendVerifiedAt: readyAt,
            backendVerifiedProjectId: projectId,
            backendVerifiedUpdatedAt: verified.updatedAt ?? verified.updated_at ?? null,
            updated_at: readyAt,
        });
        project = await targetDb.projects.get(projectId);
        return {
            project,
            backendProject: verified,
            backendVerified: true,
            documentsMigrated: Number(migration?.migrated || 0),
            displayMeta: buildQueueDisplayMeta(project),
        };
    } catch (error) {
        throw backendSyncError(error, projectId);
    }
}

export const _internals = {
    UUID_RE,
    leafName,
    isUsableDisplayName,
    firstNonBlank,
    hasTransientDocumentBinary,
    authoritativeInputChangedLanes,
    backendRecordHasStartedGeneration: projectHasGeneratedWork,
};
